"use client"

import { useMemo, useState } from "react"
import { BookOpen, TriangleAlert } from "lucide-react"
import SecondarySideBar from "@/components/shared/SecondarySideBar"
import { TruncatedCell } from "@/components/shared/TruncatedCell"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import { useCollection } from "./CollectionContext"
import type { BiblioCollection } from "@/services/biblioService"

/** Expanded width of the collection panel; wide enough for long collection names. */
const PANEL_WIDTH = 320

/**
 * Count summary shown under each collection.
 *
 * @param collection - The collection to summarise.
 * @returns A short human-readable count label, e.g. "12 sources".
 */
function buildCountLabel(collection: BiblioCollection): string {
  const sourceCount: number = collection.sourceIds?.length ?? 0
  return `${sourceCount} source${sourceCount === 1 ? "" : "s"}`
}

/** Fallback label shown for a collection whose name is missing. */
const UNNAMED_LABEL = "(unnamed collection)"

/**
 * A single selectable collection row rendered as an ARIA radio.
 *
 * @param collection - The collection this row represents.
 * @param isSelected - Whether this row is the current selection.
 * @param onSelect - Called with the collection id when the row is chosen.
 * @returns The rendered radio row element.
 */
function CollectionRow({
  collection,
  isSelected,
  onSelect,
}: {
  collection: BiblioCollection
  isSelected: boolean
  onSelect: (collectionId: number) => void
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      onClick={() => onSelect(collection.id)}
      className={cn(
        "w-full text-left rounded-md border px-2.5 py-2 transition-colors cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isSelected
          ? "border-primary bg-primary/5"
          : "border-border hover:bg-muted/50"
      )}
    >
      <div className="flex items-start gap-2">
        <span
          aria-hidden="true"
          className={cn(
            "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
            isSelected ? "border-primary" : "border-muted-foreground/40"
          )}
        >
          {isSelected && <span className="size-2 rounded-full bg-primary" />}
        </span>
        <span className="min-w-0 flex-1">
          <TruncatedCell
            value={collection.collectionName || UNNAMED_LABEL}
            className={cn(
              "text-sm font-medium",
              collection.collectionName ? "text-foreground" : "italic text-muted-foreground"
            )}
          />
          <TruncatedCell
            value={
              collection.collectionType
                ? `${buildCountLabel(collection)} · ${collection.collectionType}`
                : buildCountLabel(collection)
            }
            className="mt-0.5 text-xs text-muted-foreground"
          />
        </span>
      </div>
    </button>
  )
}

/**
 * Right-hand panel for the deep literature review chat that lists the available
 * collections (biblio collections) and lets the user select exactly one, or
 * none.
 * @returns The rendered collection side panel.
 */
export function CollectionSidebar(): React.JSX.Element {
  const {
    collections,
    selectedCollectionId,
    selectCollection,
    loading,
    error,
  } = useCollection()

  const [searchTerm, setSearchTerm] = useState<string>("")

  // Case-insensitive filter over the collection name. Kept purely local; the
  // full list stays in context so the selection survives filtering.
  const filteredCollections: BiblioCollection[] = useMemo(() => {
    const term: string = searchTerm.trim().toLowerCase()
    if (!term) {
      return collections
    }
    return collections.filter((c) =>
      (c.collectionName ?? "").toLowerCase().includes(term)
    )
  }, [collections, searchTerm])

  const hasSelection: boolean = selectedCollectionId !== null

  return (
    <SecondarySideBar
      side="right"
      width={PANEL_WIDTH}
      defaultOpen={false}
      sidebarTitle="Collections"
      collapsedExpandIcon={
        <span className="relative">
          <BookOpen className="size-4" />
          {hasSelection && (
            <span
              aria-hidden="true"
              className="absolute -right-1 -top-1 size-2 rounded-full bg-primary"
            />
          )}
        </span>
      }
    >
      <div
        role="radiogroup"
        aria-label="Collection selection"
        className="flex min-h-0 flex-1 flex-col gap-2 px-3 pb-3"
      >
        <p className="text-xs text-muted-foreground">
          Select a collection to focus the assistant on that set of sources,
          or leave it as None to search the full corpus.
        </p>

        <Input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search collections"
          aria-label="Search collections"
          className="h-8"
          disabled={loading || Boolean(error)}
        />

        {/* "None" option — the explicit way to clear the single selection. */}
        <button
          type="button"
          role="radio"
          aria-checked={!hasSelection}
          onClick={() => selectCollection(null)}
          className={cn(
            "flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors cursor-pointer",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            !hasSelection
              ? "border-primary bg-primary/5"
              : "border-border hover:bg-muted/50"
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              "flex size-4 shrink-0 items-center justify-center rounded-full border",
              !hasSelection ? "border-primary" : "border-muted-foreground/40"
            )}
          >
            {!hasSelection && <span className="size-2 rounded-full bg-primary" />}
          </span>
          <span className="text-sm font-medium text-foreground">
            None (full corpus)
          </span>
        </button>

        {/* Scrollable list of collections. */}
        <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-0.5">
          {loading ? (
            <div className="space-y-1.5" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-md" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-1.5 px-2 py-6 text-center">
              <TriangleAlert className="size-5 text-destructive" />
              <p className="text-sm font-medium text-foreground">
                Couldn't load collections
              </p>
              <p className="text-xs text-muted-foreground">{error}</p>
            </div>
          ) : collections.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              No collections are available.
            </p>
          ) : filteredCollections.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              No collections match "{searchTerm}".
            </p>
          ) : (
            filteredCollections.map((collection) => (
              <CollectionRow
                key={collection.id}
                collection={collection}
                isSelected={collection.id === selectedCollectionId}
                onSelect={selectCollection}
              />
            ))
          )}
        </div>
      </div>
    </SecondarySideBar>
  )
}
